import { isGroupShape } from '@/lib/groups/group-kind'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { expandGroupDescendants } from './selection-scope'

/**
 * `candidates` topmost first, in the order the canvas paints them (WMPCM-13465).
 *
 * The canvas stacks shapes by `zIndexById` (the unified layer order), not by the manager's list:
 * Arrange reorders only the layer order, and a new group row goes to the top of it while the list
 * keeps it just above its members. A group therefore sits at its topmost member's layer, which is
 * where it shows. Shapes missing from the map paint above the rest, as `shapeLayerPresentation`
 * places them. Ties, and every shape when there is no map, keep the list order.
 */
export function sortTopmostFirst(
  candidates: readonly ShapeData[],
  allShapes: readonly ShapeData[],
  zIndexById: ReadonlyMap<string, number> | undefined
): ShapeData[] {
  const listIndexById = new Map(allShapes.map((shape, index) => [shape.id, index]))
  const zIndexOf = (id: string) => zIndexById?.get(id) ?? (zIndexById ? zIndexById.size + 1 : 0)
  const paintLayerOf = (shape: ShapeData) => {
    if (!zIndexById || !isGroupShape(shape)) return zIndexOf(shape.id)

    const memberIds = expandGroupDescendants([shape.id], [...allShapes]).filter(
      (id) => id !== shape.id
    )

    return memberIds.length > 0 ? Math.max(...memberIds.map(zIndexOf)) : zIndexOf(shape.id)
  }
  const ranked = candidates.map((shape) => ({
    shape,
    layer: paintLayerOf(shape),
    listIndex: listIndexById.get(shape.id) ?? -1,
  }))

  ranked.sort((a, b) => b.layer - a.layer || b.listIndex - a.listIndex)
  return ranked.map(({ shape }) => shape)
}
